import { Button, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useDeletePaymentTermsTemplate,
  usePaymentTermsTemplatesQuery,
} from "../api/queries";
import { PaymentTermsTemplateTable } from "../ui/PaymentTermsTemplateTable";

export function PaymentTermsTemplatesPage() {
  const nav = useNavigate({ from: "/app/performance/payment-terms-templates" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/performance/payment-terms-templates",
  });
  const list = usePaymentTermsTemplatesQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });
  const canCreate = useActionPermission(["performance-payment-terms:create"]);
  const deleteTemplate = useDeletePaymentTermsTemplate();

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ແມ່ແບບເງື່ອນໄຂການຊຳລະ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              ແມ່ແບບເງື່ອນໄຂການຊຳລະ
            </h2>
            <p className="text-muted-foreground">
              ຕັ້ງເນື້ອຫາແຍກສຳລັບເອກະສານມີພາສີ / ບໍ່ມີພາສີ
            </p>
          </div>
          {canCreate && (
            <Button
              onClick={() =>
                nav({ to: "/app/performance/payment-terms-templates/create" })
              }
            >
              <PlusIcon className="size-4" /> ເພີ່ມແມ່ແບບ
            </Button>
          )}
        </div>

        <div className="rounded-xl border bg-card pt-2">
          <PaymentTermsTemplateTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(item) =>
              nav({
                to: "/app/performance/payment-terms-templates/$id/edit",
                params: { id: item.id },
              })
            }
            onDelete={async (id) => {
              try {
                await deleteTemplate.run(id);
                toast.success("ລຶບແມ່ແບບສຳເລັດ");
              } catch {}
            }}
          />
        </div>
      </Main>
    </>
  );
}
